import { LitElement, PropertyValues, html, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { guardedCustomElement } from '../utils/guarded-custom-element';
import { styles } from './sds-notification.styles';
import { SheriffEvent, SDS_COLOR_PALETTES } from '../types';
import { SDSEventDispatcherMixin } from '../mixins/sds-event-dispatcher-mixin';

import '../sds-icon/sds-icon';
import '../sds-button/sds-button';

type NotificationVariant = Extract<SDS_COLOR_PALETTES, 'info' | 'success' | 'attention' | 'warning' | 'critical' | 'danger'> | 'critical';

const notificationIcons: Record<NotificationVariant, string> = {
	info: 'InformationSquareFill-aksel-filled',
	success: 'check_circle-material-filled',
	attention: 'warning-material-filled',
	warning: 'warning-material-filled',
	danger: 'dangerous-material-filled',
	critical: 'dangerous-material-filled', // Deprecated
};

export interface SDSNotificationProps {
	variant?: NotificationVariant;
	'removable-label'?: string;
}

let hasWarnedAboutDeprecation: boolean = false;

@guardedCustomElement('sds-notification')
export class SDSNotification extends SDSEventDispatcherMixin(LitElement) {
	// Styling
	static override styles = [styles];

	// Reactive Props
	@property({ type: String, reflect: true }) variant: NotificationVariant = 'info';
	@property({ type: String, attribute: 'removable-label', reflect: true }) removableLabel?: string;

	constructor() {
		super();
		if (!hasWarnedAboutDeprecation) {
			hasWarnedAboutDeprecation = true;
			console.warn('[Sheriff] The Notification component is deprecated and will be removed in the next major release. Please migrate to the Alert component.');
		}
	}

	override updated(changed: PropertyValues) {
		// Cast critical variant to danger and warning to attention to prevent breaking changes
		if (changed.has('variant') && this.variant === 'critical') {
			this.variant = 'danger';
		} else if (changed.has('variant') && this.variant === 'warning') {
			this.variant = 'attention';
		}
	}

	private readonly _remove = () => {
		this.dispatchSheriffEvent('sds-remove');
	};

	override render() {
		const notificationClasses = {
			'sds-notification': true,
		};

		const colorPalette = this.variant === 'danger' || this.variant === 'critical' ? 'danger' : 'accent';

		return html`<div class="${classMap(notificationClasses)}">
			<div class="sds-notification__type">
				<sds-icon class="sds-notification__icon" name="${notificationIcons[this.variant]}" size="md"></sds-icon>
			</div>
			<div class="sds-notification__content">
				<slot name="title"></slot>
				<slot name="message"></slot>
			</div>
			${this.removableLabel
				? html`<div class="sds-notification__actions">
						<sds-button icon variant="tertiary" color="${colorPalette}" size="sm" @click=${this._remove} aria-label="${this.removableLabel}">
							<sds-icon name="close-material-outlined" size="sm"></sds-icon>
						</sds-button>
					</div>`
				: nothing}
		</div>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'sds-notification': SDSNotification;
	}
	interface GlobalEventHandlersEventMap {
		'sds-remove': SheriffEvent;
	}
}
